import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { API } from "../config";
import { useLanguage } from "../contexts/LanguageContext";
import { MessageCircle, X, Send, Loader2, Sparkles } from "lucide-react";

/**
 * V179 — Agent OS AI Chatbot floating widget.
 *
 * Two modes:
 *   - "dashboard": always rendered when the logged-in user is on Dashboard.
 *   - "landing": only rendered if admin enabled it via /api/agent-os-chat/settings.
 *
 * The component is fully self-contained — drop <ChatbotWidget mode="dashboard" user={user} />
 * anywhere and it will render a floating bubble + slide-out chat panel.
 */
const STORAGE_KEY_PREFIX = "trp_aios_chat_session_";

function actionTarget(slug) {
  // slug examples: "goto:phase3-agents", "cta:signup"
  if (!slug) return null;
  const [kind, target] = slug.split(":");
  if (kind === "goto") return { type: "tab", target };
  if (kind === "cta") return { type: "cta", target };
  return null;
}

export default function ChatbotWidget({ mode = "dashboard", user = null,
                                        onAction = null }) {
  const { t, language } = useLanguage();
  const [open, setOpen] = useState(false);
  const [enabled, setEnabled] = useState(mode === "dashboard");
  const [sessionId, setSessionId] = useState("");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const scrollRef = useRef(null);

  // Probe landing-enabled flag from server (cached for the page lifetime)
  useEffect(() => {
    let cancelled = false;
    if (mode === "landing") {
      axios.get(`${API}/agent-os-chat/settings`)
        .then(r => { if (!cancelled) setEnabled(Boolean(r.data?.landing_enabled)); })
        .catch(() => { if (!cancelled) setEnabled(false); });
    } else {
      setEnabled(true);
    }
    return () => { cancelled = true; };
  }, [mode]);

  // Restore (or create) session id for this user+mode combo
  useEffect(() => {
    const key = STORAGE_KEY_PREFIX + mode + "_" + (user?.email || "anon");
    let sid = "";
    try { sid = window.localStorage.getItem(key) || ""; } catch {}
    if (!sid) {
      sid = `aios_${mode}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      try { window.localStorage.setItem(key, sid); } catch {}
    }
    setSessionId(sid);
  }, [mode, user?.email]);

  // Pull history when widget opens
  useEffect(() => {
    if (!open || !sessionId) return;
    axios.get(`${API}/agent-os-chat/history/${sessionId}`)
      .then(r => setMessages(r.data?.messages || []))
      .catch(() => setMessages([]));
  }, [open, sessionId]);

  // Auto-scroll on new messages
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  const handleAction = useCallback((slug) => {
    const tgt = actionTarget(slug);
    if (!tgt) return;
    if (tgt.type === "tab") {
      if (onAction) {
        onAction(tgt.target);
      } else {
        // Fallback: hash navigation
        window.location.hash = `#tab=${tgt.target}`;
      }
      setOpen(false);
    } else if (tgt.type === "cta") {
      if (tgt.target === "signup") window.location.href = "/register";
      else if (tgt.target === "demo") window.location.href = "/demo";
    }
  }, [onAction]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setError("");
    setSending(true);
    // Optimistic user message
    const userTurn = { role: "user", content: text, created_at: new Date().toISOString() };
    setMessages(m => [...m, userTurn]);
    setInput("");
    try {
      const res = await axios.post(`${API}/agent-os-chat/message`, {
        session_id: sessionId,
        message: text,
        mode,
        user_email: user?.email || null,
        language,
      });
      setMessages(m => [...m, {
        role: "assistant",
        content: res.data?.reply || "",
        actions: res.data?.actions || [],
        created_at: new Date().toISOString(),
      }]);
      if (res.data?.session_id && res.data.session_id !== sessionId) {
        setSessionId(res.data.session_id);
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || t.cb_error_generic;
      setError(typeof msg === "string" ? msg : t.cb_error_generic);
    } finally {
      setSending(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  if (!enabled) return null;

  return (
    <>
      {/* Floating bubble */}
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-50 inline-flex items-center gap-2 bg-gradient-to-br from-violet-600 to-fuchsia-600 text-white rounded-full px-5 py-3.5 shadow-2xl hover:shadow-violet-500/30 transition-all hover:-translate-y-0.5"
          data-testid="chatbot-open-button"
          aria-label={t.cb_open_chat}
        >
          <Sparkles className="w-4 h-4" />
          <span className="text-sm font-bold">{t.cb_ask_pulseai}</span>
        </button>
      )}

      {/* Panel */}
      {open && (
        <div
          className="fixed bottom-6 right-6 z-50 w-[min(92vw,400px)] bg-white rounded-2xl shadow-2xl border border-gray-200 flex flex-col overflow-hidden"
          style={{ height: "min(82vh, 580px)" }}
          data-testid="chatbot-panel"
        >
          {/* Header */}
          <div className="bg-gradient-to-br from-violet-600 to-fuchsia-600 text-white px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                <Sparkles className="w-4 h-4" />
              </div>
              <div>
                <div className="text-sm font-bold leading-tight">PulseAI</div>
                <div className="text-[10px] opacity-80 leading-tight">{t.cb_subtitle}</div>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="text-white/80 hover:text-white"
              aria-label={t.cb_close_chat}
              data-testid="chatbot-close-button"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Messages */}
          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto px-4 py-3 space-y-3 bg-gray-50"
            data-testid="chatbot-messages"
          >
            {messages.length === 0 && (
              <div className="text-center text-xs text-gray-500 py-6">
                <p className="font-semibold text-gray-700 text-sm mb-1">{t.cb_greeting_title}</p>
                <p>{t.cb_greeting}</p>
              </div>
            )}
            {messages.map((m, i) => (
              <Message key={i} m={m} onAction={handleAction} />
            ))}
            {sending && (
              <div className="flex items-center gap-1.5 text-xs text-violet-600 font-semibold">
                <Loader2 className="w-3 h-3 animate-spin" /> {t.cb_thinking}
              </div>
            )}
            {error && (
              <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded p-2">
                {error}
              </div>
            )}
          </div>

          {/* Input */}
          <div className="border-t border-gray-200 bg-white p-3">
            <div className="flex items-end gap-2">
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKey}
                placeholder={t.cb_input_placeholder}
                rows={1}
                className="flex-1 resize-none border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-violet-500/40 focus:border-violet-500"
                disabled={sending}
                data-testid="chatbot-input"
              />
              <button
                type="button"
                onClick={send}
                disabled={!input.trim() || sending}
                className="bg-violet-600 disabled:bg-gray-200 text-white rounded-lg p-2 transition-colors hover:bg-violet-700"
                data-testid="chatbot-send-button"
                aria-label={t.cb_send}
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function Message({ m, onAction }) {
  const isUser = m.role === "user";
  const txt = String(m.content || "").replace(/\[\[ACTION:[^\]]+\]\]/g, "").trim();
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div
        className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm leading-relaxed ${
          isUser
            ? "bg-violet-600 text-white rounded-br-md"
            : "bg-white border border-gray-200 text-gray-800 rounded-bl-md shadow-sm"
        }`}
        data-testid={isUser ? "chatbot-user-msg" : "chatbot-assistant-msg"}
      >
        <div className="whitespace-pre-wrap">{txt}</div>
        {!isUser && Array.isArray(m.actions) && m.actions.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-2">
            {m.actions.map((a, idx) => (
              <button
                key={idx}
                type="button"
                onClick={() => onAction(a.slug)}
                className="text-[11px] bg-violet-50 hover:bg-violet-100 text-violet-700 font-bold rounded-full px-3 py-1 border border-violet-200 transition-colors"
                data-testid={`chatbot-action-${a.slug}`}
              >
                {a.label} →
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
